import {forwardRef} from 'react'
import {cn} from 'utils/cn'

/**
 * Magic UI — Shimmer Button (tailwind-v3 branch), ported to JS.
 * https://magicui.design/docs/components/shimmer-button
 *
 * A light travels around the button's perimeter, showing through the
 * `shimmerSize` gap between the outer shell and the inner backdrop.
 * Needs the `shimmer-slide` and `spin-around` keyframes in
 * tailwind.config.js. Deviations from upstream: no dark-mode classes
 * (the app is light-only), animations are `motion-safe:`, and the spark
 * container carries `data-shimmer-spark` so a variant can hide it.
 */
export const ShimmerButton = forwardRef(
  (
    {
      shimmerColor = '#ffffff',
      shimmerSize = '0.05em',
      shimmerDuration = '3s',
      borderRadius = '100px',
      background = 'rgba(0, 0, 0, 1)',
      className,
      children,
      ...props
    },
    ref
  ) => (
    <button
      type="button"
      style={{
        '--spread': '90deg',
        '--shimmer-color': shimmerColor,
        '--radius': borderRadius,
        '--speed': shimmerDuration,
        '--cut': shimmerSize,
        '--bg': background,
      }}
      className={cn(
        'group relative z-0 flex cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap border border-white/10 px-6 py-3 text-white [background:var(--bg)] [border-radius:var(--radius)]',
        'transform-gpu transition-transform duration-300 ease-in-out active:translate-y-px',
        className
      )}
      ref={ref}
      {...props}
    >
      {/* spark container */}
      <div
        data-shimmer-spark=""
        className="absolute inset-0 -z-30 overflow-visible blur-[2px] [container-type:size]"
      >
        {/* spark */}
        <div className="absolute inset-0 h-[100cqh] motion-safe:animate-shimmer-slide [aspect-ratio:1] [border-radius:0] [mask:none]">
          {/* spark before */}
          <div className="absolute -inset-full w-auto rotate-0 motion-safe:animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))] [translate:0_0]" />
        </div>
      </div>
      {children}

      {/* Highlight */}
      <div
        className={cn(
          'absolute inset-0 size-full',
          'rounded-2xl px-4 py-1.5 text-sm font-medium shadow-[inset_0_-8px_10px_#ffffff1f]',
          'transform-gpu transition-all duration-300 ease-in-out',
          'group-hover:shadow-[inset_0_-6px_10px_#ffffff3f]',
          'group-active:shadow-[inset_0_-10px_10px_#ffffff3f]'
        )}
      />

      {/* backdrop */}
      <div className="absolute -z-20 [background:var(--bg)] [border-radius:var(--radius)] [inset:var(--cut)]" />
    </button>
  )
)

ShimmerButton.displayName = 'ShimmerButton'

export default ShimmerButton
